import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getConsent,
  setConsent,
  subscribeOpenBanner,
} from "../lib/cookie-consent";

/**
 * CookieConsentBanner — GDPR consent UI for OnSports.
 * Stile coerente con il sistema OnSports: dodgers (#003143), lime, no
 * rounded corners ("sharp edges editorial"), font-condensed eyebrows,
 * Hanken Grotesk body.
 */
const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [prefs, setPrefs] = useState({ statistics: false, marketing: false });

  useEffect(() => {
    const stored = getConsent();
    if (!stored) {
      setShowBanner(true);
    } else {
      setPrefs({ statistics: stored.statistics, marketing: stored.marketing });
    }
    const unsubscribe = subscribeOpenBanner(() => {
      const current = getConsent();
      if (current) {
        setPrefs({
          statistics: current.statistics,
          marketing: current.marketing,
        });
      }
      setShowModal(true);
    });
    return unsubscribe;
  }, []);

  const acceptAll = () => {
    setConsent({ statistics: true, marketing: true });
    setShowBanner(false);
    setShowModal(false);
  };

  const rejectAll = () => {
    setConsent({ statistics: false, marketing: false });
    setShowBanner(false);
    setShowModal(false);
  };

  const savePreferences = () => {
    setConsent(prefs);
    setShowBanner(false);
    setShowModal(false);
  };

  if (!showBanner && !showModal) return null;

  return (
    <>
      {/* BANNER STICKY BOTTOM */}
      {showBanner && !showModal && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 bg-dodgers text-galactico border-t-2 border-lime"
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-desc"
          data-testid="cookie-banner"
        >
          <div className="container-os py-6 md:py-7">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between md:gap-8">
              <div className="md:max-w-[640px]">
                <p
                  id="cookie-banner-title"
                  className="font-condensed uppercase tracking-widest text-[12px] text-lime mb-3"
                >
                  ● Cookie &amp; Privacy
                </p>
                <p
                  id="cookie-banner-desc"
                  className="text-[14px] md:text-[15px] leading-[1.55] opacity-90"
                  style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
                >
                  Utilizziamo cookie tecnici necessari al funzionamento del sito
                  e, previo tuo consenso, cookie analitici (Google Analytics,
                  Microsoft Clarity) e di marketing (LeadInfo) per migliorare la
                  nostra offerta.{" "}
                  <Link
                    to="/cookie-policy"
                    className="text-lime underline underline-offset-4 hover:text-galactico transition-colors"
                  >
                    Cookie Policy
                  </Link>{" "}
                  ·{" "}
                  <Link
                    to="/privacy-policy"
                    className="text-lime underline underline-offset-4 hover:text-galactico transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row md:flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowModal(true)}
                  className="font-condensed uppercase tracking-widest text-[12px] px-5 py-3 border border-galactico/40 text-galactico hover:border-lime hover:text-lime transition-colors"
                  data-testid="cookie-customize-btn"
                >
                  Personalizza
                </button>
                <button
                  type="button"
                  onClick={rejectAll}
                  className="font-condensed uppercase tracking-widest text-[12px] px-5 py-3 border border-galactico/40 text-galactico hover:border-lime hover:text-lime transition-colors"
                  data-testid="cookie-reject-btn"
                >
                  Rifiuta tutto
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="font-condensed uppercase tracking-widest text-[12px] px-5 py-3 bg-lime text-dodgers hover:bg-galactico hover:text-dodgers transition-colors font-bold"
                  data-testid="cookie-accept-btn"
                >
                  Accetta tutto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODALE PERSONALIZZAZIONE */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-dodgers/85 p-4"
          role="dialog"
          aria-labelledby="cookie-modal-title"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget && !showBanner) setShowModal(false);
          }}
          data-testid="cookie-modal"
        >
          <div className="w-full max-w-2xl bg-galactico border-2 border-dodgers max-h-[90vh] flex flex-col">
            <div className="px-6 md:px-8 py-6 bg-dodgers text-galactico border-b-2 border-lime">
              <p className="font-condensed uppercase tracking-widest text-[12px] text-lime mb-2">
                ● Preferenze cookie
              </p>
              <h2
                id="cookie-modal-title"
                className="font-display leading-[1.05]"
                style={{ fontSize: "clamp(24px, 3vw, 32px)" }}
              >
                Gestisci le tue preferenze
              </h2>
            </div>

            <div
              className="px-6 md:px-8 py-7 overflow-y-auto flex-1 text-dodgers"
              style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
            >
              <p className="text-[15px] leading-[1.6] mb-6">
                Puoi scegliere quali categorie di cookie accettare. Il consenso
                può essere modificato o revocato in qualsiasi momento. I cookie
                tecnici necessari sono sempre attivi perché indispensabili al
                funzionamento del sito.
              </p>

              {/* COOKIE NECESSARI */}
              <div className="mb-4 border border-dodgers/20 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-bold uppercase tracking-wide text-[15px] text-dodgers mb-2">
                      Cookie necessari
                    </h3>
                    <p className="text-[14px] leading-[1.55] text-dodgers/75">
                      Indispensabili al funzionamento del sito (gestione
                      sessione, preferenze utente, gestione consenso). Non
                      possono essere disattivati.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="inline-block bg-dodgers text-lime font-condensed uppercase tracking-widest text-[10px] px-3 py-1.5">
                      Sempre attivi
                    </span>
                  </div>
                </div>
              </div>

              {/* COOKIE STATISTICA */}
              <div className="mb-4 border border-dodgers/20 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-bold uppercase tracking-wide text-[15px] text-dodgers mb-2">
                      Cookie analitici
                    </h3>
                    <p className="text-[14px] leading-[1.55] text-dodgers/75">
                      Google Analytics 4, Microsoft Clarity. Raccolta in forma
                      anonima di statistiche di traffico e comportamento per
                      migliorare il sito.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={prefs.statistics}
                        onChange={(e) =>
                          setPrefs({ ...prefs, statistics: e.target.checked })
                        }
                        data-testid="cookie-toggle-statistics"
                      />
                      <div className="h-6 w-12 bg-dodgers/20 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:bg-galactico after:border after:border-dodgers after:transition-all peer-checked:bg-lime peer-checked:after:translate-x-6 peer-checked:after:border-dodgers" />
                    </label>
                  </div>
                </div>
              </div>

              {/* COOKIE MARKETING */}
              <div className="mb-2 border border-dodgers/20 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-bold uppercase tracking-wide text-[15px] text-dodgers mb-2">
                      Cookie di marketing
                    </h3>
                    <p className="text-[14px] leading-[1.55] text-dodgers/75">
                      LeadInfo. Identificazione anonima delle aziende che
                      visitano il sito, finalizzata ad attività commerciali B2B.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        className="peer sr-only"
                        checked={prefs.marketing}
                        onChange={(e) =>
                          setPrefs({ ...prefs, marketing: e.target.checked })
                        }
                        data-testid="cookie-toggle-marketing"
                      />
                      <div className="h-6 w-12 bg-dodgers/20 after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:bg-galactico after:border after:border-dodgers after:transition-all peer-checked:bg-lime peer-checked:after:translate-x-6 peer-checked:after:border-dodgers" />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end px-6 md:px-8 py-5 bg-galactico border-t border-dodgers/20">
              <button
                type="button"
                onClick={rejectAll}
                className="font-condensed uppercase tracking-widest text-[12px] px-5 py-3 border border-dodgers/40 text-dodgers hover:border-dodgers transition-colors"
                data-testid="cookie-modal-reject-btn"
              >
                Rifiuta tutto
              </button>
              <button
                type="button"
                onClick={savePreferences}
                className="font-condensed uppercase tracking-widest text-[12px] px-5 py-3 border-2 border-dodgers text-dodgers hover:bg-dodgers hover:text-galactico transition-colors"
                data-testid="cookie-modal-save-btn"
              >
                Salva preferenze
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="font-condensed uppercase tracking-widest text-[12px] px-5 py-3 bg-lime text-dodgers font-bold hover:bg-dodgers hover:text-lime transition-colors"
                data-testid="cookie-modal-accept-btn"
              >
                Accetta tutto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentBanner;
